class CollectionTabsAccordion extends HTMLElement {
  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('.collection-tabs__panel'));
    this._observers = [];

    this.panels.forEach((panel) => {
      const handle = panel.querySelector('.collection-tabs__handle');
      if (handle) {
        handle.addEventListener('click', () => {
          if (!panel.hasAttribute('data-active')) this.activatePanel(panel);
        });
      }
      if (panel.hasAttribute('data-active')) this.initScrollIndicator(panel);
    });
  }

  activatePanel(target) {
    const track = this.querySelector('.collection-tabs__track');
    track?.setAttribute('data-drag-lock', '');
    clearTimeout(this._dragLockTimeout);
    this._dragLockTimeout = setTimeout(() => track?.removeAttribute('data-drag-lock'), 900);

    this.panels.forEach((panel) => {
      const handle = panel.querySelector('.collection-tabs__handle');
      const content = panel.querySelector('.collection-tabs__content');
      const isTarget = panel === target;

      if (isTarget) {
        panel.setAttribute('data-active', '');
        handle?.setAttribute('tabindex', '-1');
        handle?.setAttribute('aria-hidden', 'true');
        content?.removeAttribute('aria-hidden');
        this.initScrollIndicator(panel);
      } else {
        panel.removeAttribute('data-active');
        handle?.removeAttribute('tabindex');
        handle?.removeAttribute('aria-hidden');
        content?.setAttribute('aria-hidden', 'true');
      }
    });

    if (track) this.scrollTrackToPanel(track, target);
  }

  scrollTrackToPanel(track, target, duration = 600) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const paddingStart = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
    const startScroll = track.scrollLeft;
    const targetScroll = target.offsetLeft - paddingStart;

    if (reduceMotion) {
      track.scrollLeft = targetScroll;
      return;
    }

    const distance = targetScroll - startScroll;
    const startTime = performance.now();

    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = progress < 0.5 ? 2 * progress * progress : 1 - (-2 * progress + 2) ** 2 / 2;
      track.scrollLeft = startScroll + distance * eased;
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }

  initScrollIndicator(panel) {
    if (panel.dataset.scrollInited) return;
    panel.dataset.scrollInited = 'true';

    const scroll = panel.querySelector('[data-products-scroll]');
    const track = panel.querySelector('[data-scroll-track]');
    const thumb = panel.querySelector('[data-scroll-thumb]');
    if (!scroll || !track || !thumb) return;

    const update = () => {
      const scrollable = scroll.scrollWidth - scroll.clientWidth;
      if (scrollable <= 0) {
        track.style.visibility = 'hidden';
        return;
      }
      track.style.visibility = 'visible';
      const thumbPct = (scroll.clientWidth / scroll.scrollWidth) * 100;
      const leftPct = (scroll.scrollLeft / scrollable) * (100 - thumbPct);
      thumb.style.width = thumbPct + '%';
      thumb.style.left = leftPct + '%';
    };

    scroll.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(scroll);
    this._observers.push(ro);
    update();

    // Click on track → jump to position
    track.addEventListener('click', (e) => {
      if (e.target === thumb) return;
      const rect = track.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / rect.width;
      scroll.scrollLeft = ratio * (scroll.scrollWidth - scroll.clientWidth);
    });

    // Drag thumb (mouse + touch) — use AbortController so listeners are auto-removed on drag end
    let dragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    const startDrag = (clientX) => {
      dragging = true;
      startX = clientX;
      startScrollLeft = scroll.scrollLeft;
      thumb.classList.add('collection-tabs__scroll-thumb--dragging');

      const controller = new AbortController();
      const { signal } = controller;

      document.addEventListener('mousemove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        const ratio = dx / track.clientWidth;
        scroll.scrollLeft = startScrollLeft + ratio * (scroll.scrollWidth - scroll.clientWidth);
      }, { signal });

      document.addEventListener('mouseup', () => {
        dragging = false;
        thumb.classList.remove('collection-tabs__scroll-thumb--dragging');
        controller.abort();
      }, { signal });

      document.addEventListener('touchmove', (e) => {
        if (!dragging) return;
        const dx = e.touches[0].clientX - startX;
        const ratio = dx / track.clientWidth;
        scroll.scrollLeft = startScrollLeft + ratio * (scroll.scrollWidth - scroll.clientWidth);
      }, { signal, passive: true });

      document.addEventListener('touchend', () => {
        dragging = false;
        thumb.classList.remove('collection-tabs__scroll-thumb--dragging');
        controller.abort();
      }, { signal });
    };

    thumb.addEventListener('mousedown', (e) => { e.preventDefault(); startDrag(e.clientX); });
    thumb.addEventListener('touchstart', (e) => startDrag(e.touches[0].clientX), { passive: true });
  }

  disconnectedCallback() {
    if (this._observers) {
      this._observers.forEach((ro) => ro.disconnect());
      this._observers = [];
    }
  }
}

customElements.define('collection-tabs-accordion', CollectionTabsAccordion);
